import React, { useCallback } from 'react';

// DEPENDENCIES
import lastDayOfMonth from 'date-fns/lastDayOfMonth';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';

// GLOBAL FUNCTIONS
import { recountActions } from 'actions.js';
import { documentFetch, fileDownload, makePath, modalFunctions, sleep } from 'functions.js';

// HOOKS
import { useRecordContext } from 'helpers/getRecordData';

// CORE COMPONENTS
import Form from 'core/form/Form';
import Sprite from 'core/tools/Sprite';

// GLOBAL PARTS
import Sidebar from 'parts/sidebar/Sidebar';

// LOCAL PARTS
import FalloutCurrentMonth from './parts/FalloutCurrentMonth';
import Trades from './parts/Trades';

// MAIN COMPONENT
const FalloutSidebar = () => {

  // REDUX
  const dispatch = useDispatch();

  // HOOKS
  const { recordID, record: { aryTrade = [], customer_first_name, customer_last_name, inventory: { stock_num } = {}, sales_time } = {}, refetchRecord } = useRecordContext() || {};

  // CALLBACKS
  const printFalloutRecap = useCallback(
    async () => {
      documentFetch({
        endpoint: makePath(ENDPOINTS.sales.print.recap, recordID),
        accept: 'pdf',
        contentType: 'pdf',
        mimeType: 'application/pdf',
        params: {
          recapType: 'Fallout'
        },
        loadingMessage: 'Downloading Fallout Recap',
        errorMessage: 'Unable to download Fallout Recap',
        successMessage: 'Fallout Recap downloaded.',
        onSuccess: ({ url }) => {
          fileDownload(url, `${stock_num}_${customer_first_name}_${customer_last_name}_fallout_recap`, undefined, true, true);
        }
      });
    },
    [recordID, customer_first_name, customer_last_name, stock_num]
  );
  const confirmationModal = useCallback(
    () => {
      modalFunctions.add({
        type: 'confirmation',
        title: 'Fallout Confirmation',
        body: (
          <div className="d-flex align-items-center text-start">
            <Sprite use="check-circle" fill="success" className="me-2" />
            <span className="flex-grow-1">Sale status has been changed to fallout.</span>
          </div>
        ),
        continueButton: {
          label: 'Print Fallout Recap',
          icon: 'print',
          onClick: printFalloutRecap
        }
      });
    },
    [printFalloutRecap]
  );
  const handleSuccess = useCallback(
    async () => {
      refetchRecord();
      await sleep(1000);
      dispatch(recountActions.toggle());
      confirmationModal();
    },
    [dispatch, refetchRecord, confirmationModal]
  );
  const formatParams = useCallback(
    v => ({
      ...v,
      aryTradeFallout: aryTrade?.length ? aryTrade.map((a, i) => ({
        trade_id: a.trade_id,
        returned_trade: Array.isArray(v.aryTradeFallout[i].returned_trade)
          ? v.aryTradeFallout[i].returned_trade[0]
          : v.aryTradeFallout[i].returned_trade,
        purchase_trade_stock_num: v?.aryTradeFallout[i]?.purchase_trade_stock_num || a.stock_num
      })): undefined,
      fallout_date:
        v.fallout_date || (v.fallout_in_current_month === '0' ? lastDayOfMonth(new Date(sales_time)) : new Date())
    }),
    [aryTrade, sales_time]
  )

  // RENDER
  return createPortal(
    <Sidebar name="falloutSidebar" title="Fallout">
      <Sidebar.Form
        method="PUT"
        endpoint={ENDPOINTS.sales.fallout(recordID)}
        loadingMessage="Submitting Fallout"
        errorMessage="Unable to submit fallout"
        successMessage="Fallout submitted."
        onSuccess={handleSuccess}
        params={{
          recap_type: 'Fallout'
        }}
        formatParams={formatParams}
      >
        <Trades />
        <FalloutCurrentMonth />
        <Form.Textarea name="fallout_comment" label="Comments" />
        <Form.Hidden name="aryTradeFallout" />
        <Form.Col>
          <big className="d-flex align-items-center">
            <Sprite use="warning" fill="danger" className="me-2" />
            Please print your Fallout form after submission.
          </big>
        </Form.Col>
      </Sidebar.Form>
    </Sidebar>,
    document.body
  );
};
const FalloutSidebarButton = ({ disabled }) => (
  <Sidebar.Button name="falloutSidebar" label="Fallout" outline fullWidth={false} size="sm" disabled={disabled} />
)
FalloutSidebar.Button = FalloutSidebarButton;

// EXPORT
export default FalloutSidebar;
